import { getDocs, onSnapshot, query, Timestamp, where } from "firebase/firestore";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../providers/AuthProvider";
import { accountsKeyFor, readPrimaryCache, writePrimaryCache } from "../helperFunctions/primaryCache";
import {
  applyDocChanges,
  evictMoved,
  latestModified,
  normalisePrimary,
  primarySync,
} from "../helperFunctions/deltaSync";
import { isDeletedStub } from "../helperFunctions/dbFunctions";

// Re-asks for a few minutes before lastSync each time the listener starts, so a write whose
// server timestamp landed just before the previous sync point can't slip through the gap.
const OVERLAP_MS = 5 * 60 * 1000;
// The listener's result set is "everything modified since it started", so it grows over a long
// session - and Firestore re-reads all of it after a reconnect. Restarting it from a fresh
// lastSync every so often keeps that small.
const RESTART_AFTER_MS = 60 * 60 * 1000;
const CACHE_WRITE_DELAY_MS = 1000;

// Keeps one primaries collection (timesheets / quotes / jobsheets / restores) in sync with
// Firestore while reading as little as possible:
//   1. Show whatever is in this device's cache straight away (see primaryCache.js).
//   2. No cache yet - read the whole collection once.
//   3. Listen only for documents whose modifiedOn is newer than the last sync.
// This relies on every write to a primary bumping modifiedOn - see primaryWrites.js.
export const useDeltaSyncedCollection = (collectionRef) => {
  const collection = collectionRef.id;
  const { userData } = useContext(AuthContext);
  const owner = userData.uid;
  const accounts = userData.associatedAccounts;
  const accountsKey = accountsKeyFor(accounts);

  const [docs, setDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [generation, setGeneration] = useState(0);

  // What's currently held, so a listener restart can carry on from memory instead of the
  // (possibly not yet flushed) cache.
  const syncedRef = useRef({ key: null, docs: {}, lastSync: 0 });
  const cacheTimerRef = useRef(null);

  useEffect(() => {
    if (!owner) {
      return;
    }

    let key = `${owner}|${accountsKey}`;
    let cancelled = false;
    let unsubscribe = null;
    let startedAt = Date.now();

    const flushCache = () => {
      clearTimeout(cacheTimerRef.current);
      cacheTimerRef.current = null;
      let { docs, lastSync } = syncedRef.current;
      writePrimaryCache(collection, owner, accountsKey, lastSync, docs);
    };

    const commit = (nextDocs, lastSync) => {
      syncedRef.current = { key, docs: nextDocs, lastSync };
      setDocs(nextDocs);
      clearTimeout(cacheTimerRef.current);
      cacheTimerRef.current = setTimeout(flushCache, CACHE_WRITE_DELAY_MS);
    };

    const start = async () => {
      let docs = null;
      let lastSync = 0;

      // Announces everything loaded so the other collections drop any copy of the same uid
      // that's older - a move (eg. timesheet -> restore) keeps the uid, so a uid held by two
      // collections is always a stale leftover. Heals any duplicate that made it into a cache.
      const announceLoaded = (loaded) => {
        primarySync.publishArrivals(
          collection,
          Object.entries(loaded).map(([id, data]) => ({ id, modifiedOn: data.modifiedOn }))
        );
      };

      if (syncedRef.current.key === key) {
        ({ docs, lastSync } = syncedRef.current);
      } else {
        let cached = await readPrimaryCache(collection, owner, accountsKey);
        if (cancelled) return;

        if (cached) {
          docs = primarySync.evictSeen(cached.docs, collection);
          lastSync = cached.lastSync;
          announceLoaded(docs);
        }
      }

      if (docs) {
        commit(docs, lastSync);
        setLoading(false);
      } else {
        // First load on this device (or after a re-sync) - read everything once. Done with
        // getDocs rather than the listener so documents without a modifiedOn still come in.
        let fetchStartedAt = Date.now();
        let snapshot = await getDocs(query(collectionRef, where("createdBy", "in", accounts)));
        if (cancelled) return;

        docs = {};
        snapshot.forEach((doc) => {
          let data = normalisePrimary(doc.data());
          if (!isDeletedStub(data)) docs[doc.id] = data;
        });
        docs = primarySync.evictSeen(docs, collection);
        lastSync = latestModified(Object.values(docs)) || fetchStartedAt;
        announceLoaded(docs);

        commit(docs, lastSync);
        setLoading(false);
        primarySync.markSynced();
        primarySync.endResync(collection);
      }

      let changesQuery = query(
        collectionRef,
        where("createdBy", "in", accounts),
        where("modifiedOn", ">", Timestamp.fromMillis(lastSync - OVERLAP_MS))
      );

      unsubscribe = onSnapshot(
        changesQuery,
        // Metadata changes too, so a server reply with nothing new still counts as a sync for
        // "Last synced" - without it Firestore stays silent once the cached result is confirmed.
        { includeMetadataChanges: true },
        (snapshot) => {
          // "estimate" so this device's own not-yet-acknowledged writes still carry a usable
          // modifiedOn (they'd otherwise read as null until the server responds).
          let changes = snapshot.docChanges().map((change) => ({
            type: change.type,
            id: change.doc.id,
            data: normalisePrimary(change.doc.data({ serverTimestamps: "estimate" })),
            pending: change.doc.metadata.hasPendingWrites,
          }));

          let current = syncedRef.current;
          // evictSeen catches the source copy of a moved primary, which Firestore can deliver
          // again after the move has already been seen in the destination collection.
          let nextDocs = primarySync.evictSeen(applyDocChanges(current.docs, changes), collection);
          // Only advance from server-confirmed timestamps - a pending write's estimate comes
          // from this device's clock, which may be wrong.
          let confirmed = changes.filter((c) => !c.pending && c.type !== "removed");
          let nextLastSync = latestModified(
            confirmed.map((c) => c.data),
            current.lastSync
          );

          if (nextDocs !== current.docs || nextLastSync !== current.lastSync) {
            commit(nextDocs, nextLastSync);
          }

          let arrived = changes
            .filter((c) => c.type !== "removed" && !isDeletedStub(c.data))
            .map((c) => ({ id: c.id, modifiedOn: c.data.modifiedOn }));
          if (arrived.length > 0) primarySync.publishArrivals(collection, arrived);

          if (!snapshot.metadata.fromCache) primarySync.markSynced();
        },
        (err) => {
          console.error(`Error syncing ${collection}`, err);
        }
      );
    };

    start().catch((err) => {
      console.error(`Error loading ${collection}`, err);
      setLoading(false);
      primarySync.endResync(collection);
    });

    const restartIfStale = () => {
      if (document.visibilityState === "visible" && Date.now() - startedAt > RESTART_AFTER_MS) {
        setGeneration((g) => g + 1);
      }
    };
    let restartTimer = setInterval(restartIfStale, RESTART_AFTER_MS);
    document.addEventListener("visibilitychange", restartIfStale);

    let stopArrivals = primarySync.onArrivals(({ collection: from, entries }) => {
      if (from === collection) return;

      let current = syncedRef.current;
      let nextDocs = entries.reduce((d, e) => evictMoved(d, e.id, e.modifiedOn), current.docs);
      if (nextDocs !== current.docs) commit(nextDocs, current.lastSync);
    });

    let stopResync = primarySync.onResync(() => {
      primarySync.beginResync(collection);
      // Drop any pending cache write too - otherwise the cleanup below would flush the old
      // docs straight back into the cache that's about to be cleared.
      clearTimeout(cacheTimerRef.current);
      cacheTimerRef.current = null;
      syncedRef.current = { key: null, docs: {}, lastSync: 0 };
      setLoading(true);
      setGeneration((g) => g + 1);
    });

    return () => {
      cancelled = true;
      unsubscribe && unsubscribe();
      clearInterval(restartTimer);
      document.removeEventListener("visibilitychange", restartIfStale);
      stopArrivals();
      stopResync();
      if (cacheTimerRef.current) flushCache();
    };
    // accounts is covered by accountsKey - AuthProvider only hands out a new array when its
    // contents change, but keying on the string keeps this robust either way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, owner, accountsKey, generation]);

  const docsList = useMemo(() => Object.values(docs), [docs]);

  return { docs: docsList, loading };
};

// For Settings: when any primaries collection last heard from the server, and whether a
// "Re-sync all data" is still re-reading.
export const usePrimariesSyncStatus = () => {
  const read = () => ({
    lastSyncedAt: primarySync.getLastSyncedAt(),
    resyncing: primarySync.isResyncing(),
  });
  const [status, setStatus] = useState(read);

  useEffect(() => primarySync.onStatus(() => setStatus(read())), []);

  return status;
};
